import PropTypes from "prop-types";
import React from "react";
import { Layout, Grid as AntGrid, theme, Tooltip } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { setChangeModule } from '@/store/reducers/useModule';
import { setChangeTestProd } from '@/store/reducers/testProdSelect';
import { resetFields } from '@/store/reducers/headerContent';
// project import
import HeaderContent from "./HeaderContent";
import AppBarStyled from "./AppBarStyled";
import { useSelector, useDispatch } from "react-redux";
import Breadcrumbs from "@/components/@extended/Breadcrumbs";
import MenuItems from "@/menu-items/index";
import { useTheme } from '@/App';
import { getAccentColor } from '@/themes/antd-theme';

const { Header: AntHeader } = Layout;
const { useBreakpoint } = AntGrid;

// ==============================|| MAIN LAYOUT - HEADER ||============================== //

const Header = ({ open, handleDrawerToggle }) => {
  const { isDark } = useTheme();
  const { token } = theme.useToken();
  const screens = useBreakpoint();
  const matchDownMD = !screens.lg;
  const navigation = MenuItems();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { theme_color } = useSelector((state) => state.company_profile);
  const buttonBgColor = getAccentColor(theme_color, isDark);
  const browserPath = location?.pathname;
  const selectedModule = useSelector((state) => state.moduleView);
  const selectedTestProd = useSelector((state) => state.testProdView);
  const moduleChange = selectedModule === "prod" ? "pack" : "prod";
  const finalPrModule = selectedModule === "prod" ? "MBMR" : "MBPR";
  const headerContent = useSelector((state) => state.headerContent);
  const allowedPaths = ["/recipeMetadata", "/recipe-view", "/edit-process"];

  const HEADER_LABEL_MAX_CHARS = 25;
  const truncateLabel = (text) =>
    typeof text === "string" && text.length > HEADER_LABEL_MAX_CHARS
      ? `${text.slice(0, HEADER_LABEL_MAX_CHARS)}...`
      : text;

  const HeaderInfo = ({ data = headerContent }) => {
    if (!headerContent || Object?.keys(headerContent)?.length === 0) {
      return (
        <Breadcrumbs
          navigation={navigation}
          title
          titleBottom
          card={false}
          divider={false}
        />
      );
    } else {
      if (
        (allowedPaths?.includes(browserPath) && !headerContent?.Process) ||
        browserPath?.startsWith("/process-config")
      ) {
        return (
          <div className="flex items-center gap-12 text-sm">
            {Object?.entries(data)?.map(([key, value], index) => (
              <div
                key={key}
                className={`${index !== 0 ? "border-l pl-4" : ""}`}
              >
                <span className="text-gray-500">{key}</span>
                <div className="font-semibold">
                  {typeof value === "object" ? (
                    <>
                      <Tooltip title={value.text}>
                        <span>{truncateLabel(value.text)}</span>
                      </Tooltip>
                      {value.status && (
                        <span
                          className={`ml-2 text-xs px-2 py-0.5 rounded-md border
                      ${
                        value.status === "Running"
                          ? "bg-orange-100 text-orange-600 border-orange-300"
                          : value.status === "Completed"
                          ? "bg-green-100 text-green-600 border-green-300"
                          : "bg-gray-100 text-gray-600 border-gray-300"
                      }`}
                        >
                          {value.status}
                        </span>
                      )}
                    </>
                  ) : (
                    <Tooltip title={value}>
                      <span className="font-bold">{truncateLabel(value)}</span>
                    </Tooltip>
                  )}
                </div>
              </div>
            ))}
          </div>
        );
      } else {
        dispatch(resetFields());
        return (
          <Breadcrumbs
            navigation={navigation}
            title
            titleBottom
            card={false}
            divider={false}
          />
        );
      }
    }
  };

  // common header
  const mainHeader = (
    <div className={`px-6 py-0 h-16 flex items-center ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
      <div className="flex justify-between items-center w-full">
        <div className="flex-1">
          <div className={`w-full ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center gap-6">
              {/* Batch / Process / Operation / Batch Size */}
              <HeaderInfo />
            </div>
          </div>
        </div>

        {/* <div>
          <div className="flex items-center justify-center gap-2">
            <div>
              <img
                alt="Company Logo"
                src="https://png.pngtree.com/png-vector/20190304/ourmid/pngtree-growth-business-company-logo-png-image_728232.jpg"
                style={{ width: 42, height: 42, borderRadius: "50%" }}
              />
            </div>
            <div>
              <div className="text-lg font-semibold">
                Nexgensis
              </div>
              <div className="text-xs text-gray-500">
                Pune, Maharashtra
              </div>
            </div>
          </div>
        </div> */}

        <div>
          <div className="flex gap-2 items-center">
            <div className="flex gap-2 items-center">
              {/* MBMR/MBPR module toggle — hidden for now.
              <Tooltip
                title={`Switch to ${selectedModule === "prod" ? "MBPR" : "MBMR"}`}
                placement="bottom"
                zIndex={9999}
              >
                <button
                  className="px-4 py-2 rounded-md text-white text-sm hover:scale-110"
                  style={{ backgroundColor: buttonBgColor }}
                  onClick={() => dispatch(setChangeModule(moduleChange))}
                >
                  {finalPrModule}
                </button>
              </Tooltip>
              */}
              {/* Test / Production segmented switcher — both states visible,
                  the active one filled with the accent color. Square style. */}
              <div
                className="inline-flex items-center rounded-md p-0.5 select-none"
                style={{
                  height: 32,
                  backgroundColor: isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.06)",
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.08)"}`,
                }}
                role="tablist"
                aria-label="Test or Production mode"
              >
                {[
                  { key: "test", label: "Test" },
                  { key: "production", label: "Production" },
                ].map((opt) => {
                  const active = selectedTestProd === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => {
                        if (!active) {
                          dispatch(setChangeTestProd(opt.key));
                          // Toggling the mode routes back to the recipe list.
                          navigate("/recipe-view");
                        }
                      }}
                      className="px-3.5 h-full flex items-center rounded text-sm font-medium transition-all duration-200"
                      style={{
                        backgroundColor: active ? buttonBgColor : "transparent",
                        color: active
                          ? "#ffffff"
                          : isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
                        boxShadow: active ? "0 1px 4px rgba(0,0,0,0.2)" : "none",
                        cursor: active ? "default" : "pointer",
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <HeaderContent />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {!matchDownMD ? (
        <AppBarStyled
          open={open}
          style={{
            borderBottom: `1px solid ${isDark ? token.colorBorderSecondary : token.colorBorder}`,
          }}
        >
          {mainHeader}
        </AppBarStyled>
      ) : (
        <AntHeader
          className="fixed z-[1200]"
          style={{
            padding: 0,
            height: 64,
            background: isDark ? token.colorBgContainer : '#fff',
            borderBottom: `1px solid ${isDark ? token.colorBorderSecondary : token.colorBorder}`,
          }}
        >
          {mainHeader}
        </AntHeader>
      )}
    </>
  );
};

Header.propTypes = {
  open: PropTypes.bool,
  handleDrawerToggle: PropTypes.func,
};

export default Header;
